/**
 * GPS & Nearby Listings.
 *
 * Owns everything location-related for the map: reading the device GPS,
 * remembering the last known position, a manual city fallback when the user
 * denies permission, distance radius options and nearby sorting.
 *
 * Deliberately no turn-by-turn navigation — distance only.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { haversineKm, type MapItem } from "@/lib/map-search";
import { gpsLog } from "@/lib/gps-debug";

export interface GeoPoint {
  lat: number;
  lng: number;
}

export interface Origin extends GeoPoint {
  /** How the origin was set — GPS fix or a manually picked city. */
  source: "gps" | "city";
  label: string;
  /** GPS horizontal accuracy in metres (GPS origins only). */
  accuracy?: number;
}

/* ------------------------------------------------------------------ *
 * Distance filters & sorting
 * ------------------------------------------------------------------ */

export const DISTANCE_OPTIONS = [1, 3, 5, 10, 25, 50] as const;
export type DistanceOption = (typeof DISTANCE_OPTIONS)[number];
export const DEFAULT_RADIUS_KM: DistanceOption = 5;

export type NearbySort = "nearest" | "newest" | "price_low" | "price_high";

export const SORT_OPTIONS: { value: NearbySort; label: string }[] = [
  { value: "nearest", label: "Nearest" },
  { value: "newest", label: "Newest" },
  { value: "price_low", label: "Lowest Price" },
  { value: "price_high", label: "Highest Price" },
];

/* ------------------------------------------------------------------ *
 * Manual city fallback (used when GPS is denied or unavailable)
 * ------------------------------------------------------------------ */

export const CITY_CENTRES: { name: string; state: string; lat: number; lng: number }[] = [
  { name: "Mumbai", state: "Maharashtra", lat: 19.076, lng: 72.8777 },
  { name: "Pune", state: "Maharashtra", lat: 18.5204, lng: 73.8567 },
  { name: "Bengaluru", state: "Karnataka", lat: 12.9716, lng: 77.5946 },
  { name: "Hyderabad", state: "Telangana", lat: 17.385, lng: 78.4867 },
  { name: "Chennai", state: "Tamil Nadu", lat: 13.0827, lng: 80.2707 },
  { name: "Delhi", state: "Delhi", lat: 28.6139, lng: 77.209 },
  { name: "Gurugram", state: "Haryana", lat: 28.4595, lng: 77.0266 },
  { name: "Noida", state: "Uttar Pradesh", lat: 28.5355, lng: 77.391 },
  { name: "Ahmedabad", state: "Gujarat", lat: 23.0225, lng: 72.5714 },
  { name: "Surat", state: "Gujarat", lat: 21.1702, lng: 72.8311 },
  { name: "Jaipur", state: "Rajasthan", lat: 26.9124, lng: 75.7873 },
  { name: "Lucknow", state: "Uttar Pradesh", lat: 26.8467, lng: 80.9462 },
  { name: "Kolkata", state: "West Bengal", lat: 22.5726, lng: 88.3639 },
  { name: "Kochi", state: "Kerala", lat: 9.9312, lng: 76.2673 },
  { name: "Coimbatore", state: "Tamil Nadu", lat: 11.0168, lng: 76.9558 },
  { name: "Indore", state: "Madhya Pradesh", lat: 22.7196, lng: 75.8577 },
  { name: "Chandigarh", state: "Chandigarh", lat: 30.7333, lng: 76.7794 },
  { name: "Bhubaneswar", state: "Odisha", lat: 20.2961, lng: 85.8245 },
];

export function searchCities(query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return CITY_CENTRES.slice(0, 8);
  return CITY_CENTRES.filter(
    (c) => c.name.toLowerCase().includes(q) || c.state.toLowerCase().includes(q),
  ).slice(0, 8);
}

/* ------------------------------------------------------------------ *
 * Geolocation hook
 * ------------------------------------------------------------------ */

const STORAGE_KEY = "landoo:last-origin";

/**
 * Only manually picked cities are cached. A stale GPS fix must never be
 * restored — the live device reading is the single source of truth.
 */
function readStoredOrigin(): Origin | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Origin;
    if (parsed?.source !== "city") return null;
    return typeof parsed?.lat === "number" && typeof parsed?.lng === "number" ? parsed : null;
  } catch {
    return null;
  }
}

export type GeoStatus =
  | "idle"
  | "locating"
  | "improving"
  | "granted"
  | "denied"
  | "timeout"
  | "unavailable"
  | "error";

/** Accuracy (metres) considered a reliable fix. */
export const GOOD_ACCURACY_M = 30;
/** Above this we tell the user we are still improving the fix. */
export const POOR_ACCURACY_M = 50;
/** Minimum movement (metres) that justifies moving the marker. */
const MEANINGFUL_MOVE_M = 10;

export interface GeoFix {
  lat: number;
  lng: number;
  accuracy: number;
  timestamp: number;
}

export function useGeolocation() {
  const [origin, setOriginState] = useState<Origin | null>(null);
  const [status, setStatus] = useState<GeoStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [fix, setFix] = useState<GeoFix | null>(null);

  const watchId = useRef<number | null>(null);
  const bestFix = useRef<GeoFix | null>(null);
  const resolveRef = useRef<((o: Origin | null) => void) | null>(null);

  // Restore the last manually picked city after hydration (never during SSR).
  useEffect(() => {
    const stored = readStoredOrigin();
    if (stored) setOriginState(stored);
  }, []);

  const stopWatch = useCallback(() => {
    if (watchId.current != null && typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchId.current);
    }
    watchId.current = null;
  }, []);

  // Never leave a watcher running after unmount.
  useEffect(() => () => stopWatch(), [stopWatch]);

  const setOrigin = useCallback((next: Origin | null) => {
    setOriginState(next);
    if (typeof window === "undefined") return;
    // Only city origins are persisted; GPS fixes stay in memory only.
    if (next && next.source === "city")
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else window.localStorage.removeItem(STORAGE_KEY);
  }, []);

  const setCity = useCallback(
    (city: { name: string; lat: number; lng: number }) => {
      setError(null);
      stopWatch();
      bestFix.current = null;
      setOrigin({ lat: city.lat, lng: city.lng, source: "city", label: city.name });
    },
    [setOrigin, stopWatch],
  );

  /**
   * Starts a high-accuracy watch. Resolves with the first reliable fix
   * (accuracy <= 30 m) — or the first fix if the device never improves —
   * and keeps watching while the reading is still poor.
   */
  const locate = useCallback(
    () =>
      new Promise<Origin | null>((resolve) => {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
          setStatus("unavailable");
          setError("Location is not supported on this device.");
          resolve(null);
          return;
        }
        // Exactly one active watcher at a time.
        stopWatch();
        bestFix.current = null;
        resolveRef.current = resolve;
        setStatus("locating");
        setError(null);

        watchId.current = navigator.geolocation.watchPosition(
          (pos) => {
            const next: GeoFix = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              timestamp: pos.timestamp,
            };
            setFix(next);
            gpsLog({
              kind: "reading",
              message: "raw device reading",
              lat: next.lat,
              lng: next.lng,
              accuracy: next.accuracy,
              detail: { timestamp: next.timestamp },
            });

            const prev = bestFix.current;
            const movedM = prev
              ? haversineKm({ lat: prev.lat, lng: prev.lng }, { lat: next.lat, lng: next.lng }) *
                1000
              : Infinity;
            const better = !prev || next.accuracy < prev.accuracy || movedM >= MEANINGFUL_MOVE_M;
            if (!better) {
              gpsLog({
                kind: "decision",
                message: "reading ignored (no accuracy gain, no meaningful move)",
                lat: next.lat,
                lng: next.lng,
                accuracy: next.accuracy,
                detail: { movedM: Math.round(movedM), prevAccuracy: prev?.accuracy },
              });
              return;
            }
            gpsLog({
              kind: "decision",
              message: "reading accepted as best fix",
              lat: next.lat,
              lng: next.lng,
              accuracy: next.accuracy,
              detail: {
                movedM: Number.isFinite(movedM) ? Math.round(movedM) : "first",
                prevAccuracy: prev?.accuracy ?? null,
              },
            });

            bestFix.current = next;
            const nextOrigin: Origin = {
              lat: next.lat,
              lng: next.lng,
              accuracy: next.accuracy,
              source: "gps",
              label: "Current location",
            };
            setOrigin(nextOrigin);
            setError(null);

            if (next.accuracy > POOR_ACCURACY_M) {
              setStatus("improving");
              gpsLog({
                kind: "status",
                message: `improving accuracy (> ${POOR_ACCURACY_M} m) — not centering yet`,
                accuracy: next.accuracy,
              });
              return;
            }
            setStatus("granted");
            if (next.accuracy <= GOOD_ACCURACY_M) {
              // Reliable fix reached — settle the caller and stop the watcher.
              gpsLog({
                kind: "decision",
                message: `reliable fix (<= ${GOOD_ACCURACY_M} m) — settling and centering`,
                lat: next.lat,
                lng: next.lng,
                accuracy: next.accuracy,
              });
              stopWatch();
              resolveRef.current?.(nextOrigin);
              resolveRef.current = null;
            }
          },
          (err) => {
            let nextStatus: GeoStatus = "error";
            let message = "We couldn't get your location. Try again or search a city.";
            if (err.code === err.PERMISSION_DENIED) {
              nextStatus = "denied";
              message =
                "Location permission is blocked. Allow location in your browser settings, or search a city instead.";
            } else if (err.code === err.TIMEOUT) {
              nextStatus = "timeout";
              message =
                "Locating timed out — your GPS signal may be weak. Retry, or search a city.";
            } else if (err.code === err.POSITION_UNAVAILABLE) {
              nextStatus = "unavailable";
              message = "GPS is currently unavailable. Retry, or search a city.";
            }
            stopWatch();
            setStatus(nextStatus);
            setError(message);
            gpsLog({ kind: "error", message, detail: { code: err.code, raw: err.message } });
            const settled = bestFix.current
              ? {
                  lat: bestFix.current.lat,
                  lng: bestFix.current.lng,
                  accuracy: bestFix.current.accuracy,
                  source: "gps" as const,
                  label: "Current location",
                }
              : null;
            resolveRef.current?.(settled);
            resolveRef.current = null;
          },
          { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
        );
      }),
    [setOrigin, stopWatch],
  );

  const clearError = useCallback(() => {
    setError(null);
    setStatus((s) => (s === "locating" || s === "improving" ? s : "idle"));
  }, []);

  return {
    origin,
    setOrigin,
    setCity,
    locate,
    stopWatch,
    status,
    error,
    fix,
    accuracy: fix?.accuracy ?? null,
    clearError,
    clear: () => {
      stopWatch();
      bestFix.current = null;
      setFix(null);
      setOrigin(null);
    },
  };
}

/* ------------------------------------------------------------------ *
 * Distance + nearby
 * ------------------------------------------------------------------ */

/** Distance from the origin, falling back to the item's own value. */
export function distanceFrom(item: MapItem, origin: Origin | null): number {
  if (origin && item.lat != null && item.lng != null) {
    return haversineKm(origin, { lat: item.lat, lng: item.lng });
  }
  return item.distanceKm;
}

/** Attach a live distance to each item relative to the current origin. */
export function withDistance(items: MapItem[], origin: Origin | null): MapItem[] {
  if (!origin) return items;
  return items.map((i) => ({ ...i, distanceKm: distanceFrom(i, origin) }));
}

export function sortItems(items: MapItem[], sort: NearbySort): MapItem[] {
  const list = [...items];
  switch (sort) {
    case "nearest":
      return list.sort((a, b) => a.distanceKm - b.distanceKm);
    case "newest":
      return list.sort(
        (a, b) =>
          new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime() ||
          a.title.localeCompare(b.title),
      );
    case "price_low":
      return list.sort((a, b) => a.price - b.price);
    case "price_high":
      return list.sort((a, b) => b.price - a.price);
    default:
      return list;
  }
}

/** Radius filter + sort in one pass — the "Nearby Listings" feed. */
export function nearbyListings(
  items: MapItem[],
  origin: Origin | null,
  radiusKm: number,
  sort: NearbySort,
): MapItem[] {
  // Without a real origin there is no meaningful distance, so the radius filter
  // is skipped and "nearest" falls back to the incoming order.
  if (!origin) return sort === "nearest" ? items : sortItems(items, sort);
  const scoped = withDistance(items, origin).filter((i) => i.distanceKm <= radiusKm);
  return sortItems(scoped, sort);
}

export function formatDistance(km: number) {
  if (!Number.isFinite(km)) return "—";
  if (km < 1) return `${Math.max(50, Math.round(km * 1000))} m`;
  return `${km.toFixed(km < 10 ? 1 : 0)} km`;
}
